import { Button, Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { TaskExecution } from "shared/api";
import { getDaysOverdueCellStyle } from "shared/helpers";

interface UseCompleteMyTasksColumnsProps {
  onCompleteTask: (record: TaskExecution) => void;
}

/**
 * Columns for the "Complete my tasks" page. Mirrors the task-execution log
 * columns and adds an action column with a "Complete task" button per row.
 */
export const useCompleteMyTasksColumns = ({
  onCompleteTask,
}: UseCompleteMyTasksColumnsProps): ColumnsType<TaskExecution> => {
  const { t } = useTranslation();

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = dayjs(value);
    return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "-";
  };

  return [
    {
      title: t("taskHub.logTable.task") || "Task",
      key: "task",
      render: (_, record) => record.task?.name ?? "-",
    },
    {
      title: t("taskHub.logTable.expectedTime") || "Expected",
      dataIndex: "timestamp_expected",
      key: "timestamp_expected",
      render: (value: string) => formatDate(value),
    },
    {
      title: t("taskHub.logTable.executedTime") || "Executed",
      dataIndex: "timestamp_executed",
      key: "timestamp_executed",
      render: (value: string) => formatDate(value),
    },
    {
      title: t("taskHub.logTable.status") || "Status",
      dataIndex: "status",
      key: "status",
      render: (value: string) => <Tag>{value ?? "-"}</Tag>,
    },
    {
      title: t("taskHub.logTable.priority") || "Priority",
      dataIndex: "priority",
      key: "priority",
      align: "center",
    },
    {
      title: t("taskHub.logTable.taskLog") || "Message",
      dataIndex: "message",
      key: "message",
      render: (value: string) => value?.trim() || "-",
    },
    {
      title: t("taskHub.logTable.daysOverdue") || "Days overdue",
      dataIndex: "calc_days_overdue",
      key: "calc_days_overdue",
      align: "center",
      render: (value: number | null | undefined) =>
        value == null ? "-" : value,
      onCell: (record) => ({
        style: getDaysOverdueCellStyle(record.calc_days_overdue),
      }),
    },
    {
      title: t("taskHub.myTasks.actions") || "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          style={{ backgroundColor: themeColor, borderColor: themeColor }}
          onClick={() => onCompleteTask(record)}
        >
          {t("taskHub.myTasks.completeTask") || "Complete task"}
        </Button>
      ),
    },
  ];
};
